// Empirical-CDF calibration (pipeline Stage 6, display side).
//
// The model emits a raw, rater-relative score in [0,1] (uncalibrated). The
// app maps it to a display percentile via the reference quantiles in
// calibration.json:
//
//   percentile = interp(raw, raw_quantiles, probs)
//
// This is the JS port of the Colab reference:
//   np.interp(raw, CALIB['raw_quantiles'], CALIB['probs'])
// np.interp clamps to the endpoints outside the table range, which we mirror.
import { CALIB_PATH } from './constants';

let calibPromise = null;

// Linear interpolation of `raw` over the monotonic (xs -> ys) table, clamped
// to [ys[0], ys[last]] outside the table — identical to numpy.interp.
export function interp(raw, xs, ys) {
  const n = xs.length;
  if (!n || ys.length !== n) return null;
  if (!Number.isFinite(raw)) return null;
  if (raw <= xs[0]) return ys[0];
  if (raw >= xs[n - 1]) return ys[n - 1];
  // Binary search for the bracketing interval [xs[lo], xs[hi]].
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] <= raw) lo = mid;
    else hi = mid;
  }
  const span = xs[hi] - xs[lo];
  if (span <= 0) return ys[lo]; // degenerate (duplicate x) — take lower
  const frac = (raw - xs[lo]) / span;
  return ys[lo] + frac * (ys[hi] - ys[lo]);
}

// Build a toPercentile(raw) closure from a parsed calibration table.
export function makeToPercentile(calib) {
  const xs = (calib && calib.raw_quantiles) || [];
  const ys = (calib && calib.probs) || [];
  return (raw) => interp(raw, xs, ys);
}

// Fetch + memoize the calibration table, returning a toPercentile fn. On any
// failure the fn returns null (caller surfaces no percentile).
export function loadCalibration(fetchImpl) {
  if (!calibPromise) {
    const doFetch =
      fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    calibPromise = (async () => {
      if (!doFetch) return () => null;
      const res = await doFetch(CALIB_PATH);
      if (!res || !res.ok) throw new Error('calibration fetch failed');
      const calib = await res.json();
      return makeToPercentile(calib);
    })().catch(() => {
      calibPromise = null; // allow retry on a later scan
      return () => null;
    });
  }
  return calibPromise;
}

export function _resetForTests() {
  calibPromise = null;
}
